import {
  ENUM_VALUE_TYPE,
  ARRAY_VALUE_TYPE,
  STRING_VALUE_TYPE,
  CELL_LAYOUT_TYPE,
  LABELLED_LAYOUT_TYPE,
  makeUserEditor,
} from 'grb-webui/components/FilterBar';
import LicenseFilterEditor from '../../components/LicenseFilterEditor';
import {
  HISTORY_JOBS_TYPE,
} from '../../ComponentTypes';
import {
  JOB_STATUS_QUEUED,
  JOB_STATUS_RUNNING,
} from '../../services/JobService';
import BatchFilterEditor from './BatchFilterEditor';
import './JobFilter.scss';

const startsWithFilter = (text, accessor) => {
  if (!text) return () => true;
  const l = text.length;
  return (job) => {
    const s = accessor(job);
    return s && (s.length >= l) && (s.substring(0, l) === text);
  };
};

const JOB_LICENSE_FILTER = 'license';
const JOB_USERNAME_FILTER = 'user';
const JOB_TIMERANGE_FILTER = 'timerange';
const JOB_ID_FILTER = 'jobId';
const JOB_STATUS_FILTER = 'status';
const JOB_SOLVE_STATUS_FILTER = 'solveStatus';
const JOB_LIMIT_FILTER = 'length';
const JOB_TYPE_FILTER = 'batchId';
const JOB_APPLICATION_FILTER = 'app';
const JOB_NODE_ADDRESS_FILTER = 'address';
const JOB_ALGORITHM_FILTER = 'algorithm';
const JOB_RUNTIME_VERSION_FILTER = 'runtime';

const createFilter = ({
  createTimeRangeEditor,
  jobType,
  authService,
  jobService,
  filter = {},
  admin,
  ...options
}) => {
  const statusesByName = jobService.getJobStatuses().reduce((acc, statusNode) => {
    acc[statusNode.name] = statusNode.value;
    return acc;
  }, {});

  const solveStatusesByName = jobService.getJobSolveStatuses().reduce((acc, statusNode) => {
    acc[statusNode.name] = statusNode.value;
    return acc;
  }, {});

  const getJobBatchId = jobService.getJobBatchId.bind(jobService);
  const getJobApplication = jobService.getJobApplication.bind(jobService);
  const getJobAddress = jobService.getJobAddress.bind(jobService);
  const getJobStatus = jobService.getJobStatus.bind(jobService);
  const getJobSolveStatus = jobService.getJobSolveStatus.bind(jobService);
  const getJobUsername = jobService.getJobUsername.bind(jobService);
  const getJobStartedAt = jobService.getJobStartedAt.bind(jobService);
  const getJobEndedAt = jobService.getJobEndedAt.bind(jobService);
  const getJobParentId = jobService.getJobParentId.bind(jobService);
  const useBatch = jobService.useBatch();
  const useApplication = jobService.useApplication();
  const getJobAlgorithm = jobService.getJobAlgorithm.bind(jobService);
  const getJobRuntime = jobService.getJobRuntime.bind(jobService);

  const editorTypes = {
    [JOB_LICENSE_FILTER]: () => LicenseFilterEditor({ admin, ...options }),
    [JOB_USERNAME_FILTER]: () => makeUserEditor({
      name: JOB_USERNAME_FILTER,
      title: 'Username',
      currentUserChipValue: 'My jobs',
      authService,
      getObjectUsername: getJobUsername,
    }),
    [JOB_TIMERANGE_FILTER]: () => createTimeRangeEditor({
      title: 'Time range',
      getObjectDate: b => new Date(getJobEndedAt(b) || getJobStartedAt(b)),
    }),
    [JOB_ID_FILTER]: () => ({
      title: 'Job ID',
      name: JOB_ID_FILTER,
      type: STRING_VALUE_TYPE,
      layoutConfig: { column: '1/-1' },
      createObjectFilter: jobId => startsWithFilter(jobId, job => job.id),
    }),
    [JOB_STATUS_FILTER]: () => ({
      title: 'Job status',
      name: 'status',
      type: ARRAY_VALUE_TYPE,
      useMultipleKeys: true,
      layout: LABELLED_LAYOUT_TYPE,
      namedValues: [
        { value: undefined, label: 'All statuses' },
        ...jobService.getJobStatuses().filter(s => (
              (s.filter !== false)
              && (s.name !== JOB_STATUS_QUEUED)
              && ((jobType !== HISTORY_JOBS_TYPE) || (s.name !== JOB_STATUS_RUNNING))))
          .map(s => ({ value: s.name, label: s.label })),
      ],
      createObjectFilter: (statusNames) => {
        if (statusNames.length === 0) return () => true;
        const statusValues = statusNames.map(name => statusesByName[name]);
        return job => (statusValues.indexOf(getJobStatus(job)) >= 0);
      },
    }),
    [JOB_SOLVE_STATUS_FILTER]: () => ({
      title: 'Job solve status',
      name: 'solveStatus',
      type: ENUM_VALUE_TYPE,
      layout: CELL_LAYOUT_TYPE,
      namedValues: [
        { value: undefined, label: 'All solve job statuses' },
        ...jobService.getJobSolveStatuses()
          .map(s => ({ value: s.name, label: s.label })),
      ],
      getValueLabel(value) {
        if (!value) return 'All solve statuses';
        try {
          const nValue = parseInt(value, 10);
          const status = jobService.getJobSolveStatuses().find(s => s.value === nValue);
          return status ? status.label : 'Unkown';
        } catch (e) {
          return 'Unkown';
        }
      },
      createObjectFilter: (statusNames) => {
        if (statusNames.length === 0) return () => true;
        const statusValues = statusNames.map(name => solveStatusesByName[name]);
        return job => (statusValues.indexOf(getJobSolveStatus(job)) >= 0);
      },
    }),
    [JOB_LIMIT_FILTER]: () => ({
      title: 'Job limit',
      name: JOB_LIMIT_FILTER,
      type: ENUM_VALUE_TYPE,
      layout: CELL_LAYOUT_TYPE,
      namedValues: [
        { value: '100', label: '100 jobs' },
        { value: '200', label: '200 jobs' },
        { value: '500', label: '500 jobs' },
      ],
      defaultValue: '200',
    }),
    [JOB_TYPE_FILTER]: () => ({
      title: 'Job type',
      name: JOB_TYPE_FILTER,
      type: ENUM_VALUE_TYPE,
      layout: CELL_LAYOUT_TYPE,
      layoutConfig: { span: 2 },
      Component: BatchFilterEditor,
      namedValues: [
        { value: undefined, label: 'All' },
        { value: '-', label: 'Interactive jobs' },
        { value: '*', label: 'Batch jobs' },
      ],
      getValueLabel(value) {
        if (value === undefined) return 'All';
        if (value === '-') return 'Interactive jobs';
        if (value === '*') return 'Batch jobs';
        return `Batch ID '${value}'`;
      },
      createObjectFilter: (batch) => {
        if (batch === '-') return job => !getJobBatchId(job);
        if (batch === '*') return job => getJobBatchId(job);
        return startsWithFilter(batch, getJobBatchId);
      },
    }),
    [JOB_APPLICATION_FILTER]: () => ({
      title: 'Application',
      name: JOB_APPLICATION_FILTER,
      type: STRING_VALUE_TYPE,
      createObjectFilter: app => startsWithFilter(app, getJobApplication),
    }),
    [JOB_NODE_ADDRESS_FILTER]: () => ({
      title: 'Node',
      name: JOB_NODE_ADDRESS_FILTER,
      type: STRING_VALUE_TYPE,
      createObjectFilter: address => startsWithFilter(address, getJobAddress),
    }),
    [JOB_ALGORITHM_FILTER]: () => ({
      title: 'Algorithm',
      name: 'algorithm',
      // tab: 'machineMachineFilter',
      type: ENUM_VALUE_TYPE,
      layout: CELL_LAYOUT_TYPE,
      namedValues: [
        { value: undefined, label: 'All algorithms' },
        { value: 'SIMPLEX', label: 'SIMPLEX' },
        { value: 'MIP', label: 'MIP' },
        { value: 'BARRIER', label: 'BARRIER' },
        { value: 'TUNING', label: 'TUNING' },
      ],
      createObjectFilter: (algo) => {
        return (job) => {
          return !algo || (getJobAlgorithm(job) === algo);
        };
      },
    }),
    [JOB_RUNTIME_VERSION_FILTER]: () => ({
      title: 'Gurobi Version',
      name: 'version',
      // tab: 'optionsMachineFilter',
      type: ENUM_VALUE_TYPE,
      layout: CELL_LAYOUT_TYPE,
      canDeleteValue: () => true,
      getValueLabel: value => `Runtime: ${value}`,
      namedValues: ({ jobs }) => {
        const runtimes = [];
        jobs.forEach((job) => {
          (job.properties && job.properties.runtime && [job.properties.runtime] || []).forEach((r) => {
            runtimes[r] = true;
          });
        });
        // Sort runtimes
        // https://stackoverflow.com/questions/40201533/sort-version-dotted-number-strings-in-javascript
        const allRuntimes = Object.keys(runtimes).map(a => a.split('.')
          .map(n => +n + 100000).join('.')).sort()
          .map(a => a.split('.').map(n => +n - 100000).join('.'));
        return [{ value: undefined, label: 'All versions' }].concat(allRuntimes.map(r => (
          { value: r, label: r }
        )));
      },
      createObjectFilter: (version) => {
        if (!version) return () => true;
        return job => (getJobRuntime(job) === version);
      },
    }),
  };

  const defaultEditorNames = [
    JOB_USERNAME_FILTER,
    useApplication && JOB_APPLICATION_FILTER,
    (jobType === HISTORY_JOBS_TYPE) && JOB_TIMERANGE_FILTER,
    JOB_ID_FILTER,
    JOB_STATUS_FILTER,
    (jobType === HISTORY_JOBS_TYPE) && JOB_LIMIT_FILTER,
    useBatch && JOB_TYPE_FILTER,
    JOB_ALGORITHM_FILTER,
    (jobType !== HISTORY_JOBS_TYPE) && JOB_NODE_ADDRESS_FILTER,
  ].filter(x => x);

  const { server = jobType === HISTORY_JOBS_TYPE } = filter;
  return {
    emptyChipLabel: 'All jobs',
    dialogClassName: 'grb-jobs-dialog-filter',
    server,
    getParent: jobType === HISTORY_JOBS_TYPE ? undefined : getJobParentId,
    getId: job => job.id,
    searchKey: 'search',
    ...(filter.props || {}),
    propertyEditors: (filter.propertyEditors
      ? filter.propertyEditors(editorTypes)
      : defaultEditorNames.map(name => (name && editorTypes[name]()))
    ).filter(x => x),
  };
};

export {
  createFilter as default,
  JOB_LICENSE_FILTER,
  JOB_USERNAME_FILTER,
  JOB_TIMERANGE_FILTER,
  JOB_ID_FILTER,
  JOB_STATUS_FILTER,
  JOB_SOLVE_STATUS_FILTER,
  JOB_LIMIT_FILTER,
  JOB_TYPE_FILTER,
  JOB_APPLICATION_FILTER,
  JOB_NODE_ADDRESS_FILTER,
  JOB_ALGORITHM_FILTER,
  JOB_RUNTIME_VERSION_FILTER,
};
